import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2, ShieldCheck, RefreshCw, Search, ChevronRight,
  ClipboardCheck, Layers, TrendingUp, FileText,
} from "lucide-react";
import "./AdminUATCycles.css";
import "./AdminRegulatory.css";
import { Pill } from "@/components/regulatory/Pill";
import { FrameworkCard } from "@/components/regulatory/FrameworkCard";
import { FrameworkDetail } from "@/components/regulatory/FrameworkDetail";
import { ControlDrawer } from "@/components/regulatory/ControlDrawer";
import {
  RISK_TONE, STATUS_TONE, READINESS_TONE, READINESS_LABEL,
  FRAMEWORK_META, fmtDate,
} from "@/components/regulatory/constants";

// ============================================================================
// Main page — slim orchestrator (refactored — modules live under
// /app/frontend/src/components/regulatory/).
// ============================================================================
export default function AdminRegulatory() {
  const [frameworks, setFrameworks] = useState([]);
  const [overall, setOverall] = useState({ score: 0, readiness: "not_assessed" });
  const [controls, setControls] = useState([]);
  const [summary, setSummary] = useState(null);
  const [assessments, setAssessments] = useState([]);
  const [q, setQ] = useState("");
  const [fwFilter, setFwFilter] = useState("");
  const [riskFilter, setRiskFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(null);
  const [selectedControl, setSelectedControl] = useState(null);
  const [selectedFw, setSelectedFw] = useState(null);

  const loadFrameworksAndSummary = useCallback(async () => {
    try {
      const [fw, sum, asses] = await Promise.all([
        api.get("/admin/regulatory/frameworks"),
        api.get("/admin/regulatory/controls/summary"),
        api.get("/admin/regulatory/assessments", { params: { limit: 10 } }),
      ]);
      setFrameworks(fw.data.frameworks || []);
      setOverall({ score: fw.data.overall_score, readiness: fw.data.overall_readiness });
      setSummary(sum.data);
      setAssessments(asses.data.assessments || []);
    } catch (e) {
      toast.error(formatApiError(e, "Failed to load frameworks"));
    }
  }, []);

  const loadControls = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/regulatory/controls", {
        params: {
          q: q || undefined,
          framework: fwFilter || undefined,
          risk_level: riskFilter || undefined,
          status: statusFilter || undefined,
          limit: 500,
        },
      });
      setControls(data.controls || []);
    } catch (e) {
      toast.error(formatApiError(e, "Failed to load controls"));
    }
  }, [q, fwFilter, riskFilter, statusFilter]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([loadFrameworksAndSummary(), loadControls()]);
      setLoading(false);
    })();
  }, [loadFrameworksAndSummary, loadControls]);

  const runAssessment = async (fwId) => {
    setRunning(fwId);
    try {
      const { data } = await api.post("/admin/regulatory/assessments/run", { framework_id: fwId });
      toast.success(
        `${data.framework_id.toUpperCase()} assessment complete — ${data.results.weighted_score}% (${data.results.readiness})`,
        { duration: 6000 },
      );
      await loadFrameworksAndSummary();
    } catch (e) {
      toast.error(formatApiError(e, "Assessment failed"));
    } finally { setRunning(null); }
  };

  const refresh = () => { loadFrameworksAndSummary(); loadControls(); };

  return (
    <div className="hw-uat-cycles" data-testid="admin-regulatory">
      <div className="hw-uat-cycles__header">
        <div>
          <h2>
            <ShieldCheck className="w-5 h-5" style={{ display: "inline", marginRight: "0.4rem", verticalAlign: "text-bottom" }} />
            Regulatory Framework Engine
          </h2>
          <p className="hw-uat-cycles__subtitle">
            Shared control library with framework mappings for
            HIPAA, 42 CFR Part 2, SOC 2, and NIST CSF. Risk-weighted scoring,
            gap analysis, and on-demand + quarterly-scheduled assessments.
          </p>
        </div>
        <button type="button" className="hw-reg__btn"
                onClick={refresh} data-testid="regulatory-refresh">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      <section className="hw-reg__overall" data-testid="regulatory-overall">
        <div>
          <div className="hw-reg__overall-label">Overall Compliance Score</div>
          <div className="hw-reg__overall-score">
            <span data-testid="regulatory-overall-score">{overall.score ?? 0}</span>
            <span>%</span>
            <Pill tone={READINESS_TONE[overall.readiness]}>{READINESS_LABEL[overall.readiness]}</Pill>
          </div>
        </div>
        {summary ? (
          <div className="hw-reg__overall-stats">
            <div><strong>{summary.total}</strong> controls</div>
            <div><strong>{summary.by_risk_level?.critical || 0}</strong> critical</div>
            <div><strong>{summary.by_status?.compliant || 0}</strong> compliant</div>
            <div><strong>{summary.by_status?.not_assessed || 0}</strong> not assessed</div>
          </div>
        ) : null}
      </section>

      <section className="hw-reg__fw-grid" data-testid="regulatory-frameworks-grid">
        {frameworks.map((fw) => (
          <FrameworkCard key={fw.framework_id} framework={fw}
                         running={running} onRunAssessment={runAssessment}
                         onOpen={setSelectedFw} />
        ))}
      </section>

      {assessments.length > 0 && (
        <section className="hw-reg__assess" data-testid="regulatory-recent-assessments">
          <h3><TrendingUp className="w-4 h-4" /> Recent Assessments</h3>
          <ul>
            {assessments.slice(0, 6).map((a) => (
              <li key={a.assessment_id}>
                <code>{a.assessment_id}</code>
                <strong style={{ color: FRAMEWORK_META[a.framework_id]?.color }}>
                  {a.framework_id.toUpperCase()}
                </strong>
                <span>{a.results?.weighted_score}%</span>
                <Pill tone={READINESS_TONE[a.results?.readiness]}>{READINESS_LABEL[a.results?.readiness]}</Pill>
                <span className="hw-reg__assess-when">{fmtDate(a.triggered_at)}</span>
                <span className="hw-reg__assess-by">{a.triggered_by}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="hw-reg__controls-sec">
        <h3><Layers className="w-4 h-4" /> Compliance Control Library
          <span className="hw-reg__count">({controls.length})</span>
        </h3>
        <div className="hw-reg__filters">
          <div className="hw-reg__search">
            <Search className="w-4 h-4" />
            <input type="text" placeholder="Search control ID, title, requirement…"
                   value={q} onChange={(e) => setQ(e.target.value)}
                   data-testid="control-search" />
          </div>
          <select value={fwFilter} onChange={(e) => setFwFilter(e.target.value)}
                  data-testid="control-framework-filter">
            <option value="">All frameworks</option>
            <option value="hipaa">HIPAA</option>
            <option value="cfr42_part2">42 CFR Part 2</option>
            <option value="soc2">SOC 2</option>
            <option value="nist_csf">NIST CSF</option>
          </select>
          <select value={riskFilter} onChange={(e) => setRiskFilter(e.target.value)}
                  data-testid="control-risk-filter">
            <option value="">All risks</option>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                  data-testid="control-status-filter">
            <option value="">All statuses</option>
            <option value="compliant">Compliant</option>
            <option value="partial">Partial</option>
            <option value="non_compliant">Non-Compliant</option>
            <option value="not_assessed">Not Assessed</option>
            <option value="not_applicable">N/A</option>
          </select>
        </div>

        {loading ? (
          <div className="hw-uat-cycles__loading">
            <Loader2 className="w-5 h-5 animate-spin" /> Loading controls…
          </div>
        ) : controls.length === 0 ? (
          <div className="hw-uat-cycles__empty">No controls match your filters.</div>
        ) : (
          <div className="hw-reg__ctrl-list" data-testid="control-list">
            {controls.map((c) => (
              <button key={c.control_id} type="button"
                      className="hw-reg__ctrl-row"
                      onClick={() => setSelectedControl(c.control_id)}
                      data-testid={`control-row-${c.control_id}`}>
                <div className="hw-reg__ctrl-main">
                  <div className="hw-reg__ctrl-title">
                    <code>{c.control_id}</code>
                    <span>{c.title}</span>
                  </div>
                  <div className="hw-reg__ctrl-meta">
                    <Pill tone={RISK_TONE[c.risk_level]}>{RISK_TONE[c.risk_level]?.label}</Pill>
                    <Pill tone={STATUS_TONE[c.status]}>{STATUS_TONE[c.status]?.label}</Pill>
                    <span className="hw-reg__ctrl-category">
                      <FileText className="w-3 h-3" /> {c.category}
                    </span>
                    <span className="hw-reg__ctrl-fws">
                      {(c.framework_mappings || []).map((m, mi) => (
                        <span key={`${m.framework}-${m.requirement_id || mi}`}
                              className="hw-reg__ctrl-fw-tag"
                              style={{ color: FRAMEWORK_META[m.framework]?.color }}>
                          {m.framework.toUpperCase()}
                        </span>
                      ))}
                    </span>
                  </div>
                </div>
                <ChevronRight className="w-4 h-4" style={{ color: "#94A3B8" }} />
              </button>
            ))}
          </div>
        )}
      </section>

      {selectedControl && (
        <ControlDrawer controlId={selectedControl}
                       onClose={() => setSelectedControl(null)}
                       onSaved={refresh} />
      )}
      {selectedFw && (
        <FrameworkDetail frameworkId={selectedFw}
                         onClose={() => setSelectedFw(null)}
                         onRun={runAssessment}
                         running={running} />
      )}
    </div>
  );
}

// Named export retained so ClipboardCheck import isn't flagged as unused —
// used by the Regulatory tab icon.
export const _RegulatoryTabIcon = ClipboardCheck;  // eslint-disable-line no-unused-vars
